import { useEffect } from 'react';
import {
  useAiDocumentationPreviewQuery,
  AiDocumentationPreviewQuery,
} from 'graphql-common';
import { logger } from '@lib/services/logger';

export type DocReferenceDocumentation = AiDocumentationPreviewQuery['data'];

export type DocReferenceResult = {
  loading: boolean;
  error: boolean;
  documentation: DocReferenceDocumentation;
};

type Options = {
  // While the assistant turn is still streaming we render a placeholder and do
  // not fetch — avoids firing a query on every delta and on half-formed ids.
  skip?: boolean;
  // The `version-id` the assistant citation pinned (BE guide §5). When set, the
  // chip resolves to that exact version's file/notes instead of the document's
  // current version.
  versionId?: string;
};

// Resolves a `:documentation{id}` reference into the data DocumentationPreview
// needs via the thin `aiDocumentationPreview(id)` query — one network call per
// reference, fetching only the preview fields (name, currentVersion attachment,
// notes, read, favorited) instead of the heavy edmDocumentation payload.
// When a `versionId` is pinned, the query also fetches that version and we swap
// it in as `currentVersion` so every consumer (chip menu, viewer, notes) shows
// the version the model actually read. The pinned version is best-effort: if
// it errors or comes back null, the chip falls back to the current version.
export default function useDocReference(
  id: string,
  { skip, versionId }: Options = {},
): DocReferenceResult {
  const { data, previousData, loading, error } = useAiDocumentationPreviewQuery(
    {
      variables: {
        id,
        versionId: versionId || '',
        hasVersion: !!versionId,
      },
      skip: skip || !id,
      fetchPolicy: 'cache-first',
      // Keep `data.data` when only `pinnedVersion` errors (deleted/forbidden
      // version) — watchQuery defaults to 'none', which would drop it and
      // turn a working chip into "Document unavailable".
      errorPolicy: 'all',
    },
  );

  // A verified citation can add a `versionId` after the chip first resolved;
  // keep showing the previous result meanwhile instead of flashing a loader.
  const resolved = data ?? (loading ? previousData : undefined);
  const documentation = resolved?.data ?? null;
  const pinnedVersion = resolved?.pinnedVersion;
  const isPinnedVersionMissing =
    !!versionId && !loading && !!documentation && !pinnedVersion;

  useEffect(() => {
    if (isPinnedVersionMissing) {
      logger.warn('AiAssistant: pinned version unavailable, using current', {
        id,
        versionId,
        error: error?.message,
      });
    }
  }, [error?.message, id, isPinnedVersionMissing, versionId]);

  return {
    loading: loading && !resolved,
    error: !!error && !documentation,
    documentation:
      documentation && pinnedVersion
        ? { ...documentation, currentVersion: pinnedVersion }
        : documentation,
  };
}
